import { css } from "@linaria/core"
import {
  Box,
  Skeleton,
  Stack,
  Tab,
  tabClasses,
  Tabs,
  tabsClasses,
  TextField,
  Typography,
} from "@mui/material"
import React, { useEffect, useMemo } from "react"

import { Reserves } from "../../../ao/pool"
import { TokenInfo } from "../../../ao/token"
import { INPUT_DECIMALS } from "../../../settings"
import { NumberFont } from "../../../theme"
import {
  decimalEnforcer,
  formatNumberAuto,
  formatTicker,
  truncateNumberAuto,
} from "../../../utils/format"
import { CustomSlider } from "../../CustomSlider"
import { Input, SmallButton } from "../../TokenInput"
import TokenLogo from "../../TokenLogo"
import TokenPill from "../../TokenPill"

const _1_HOUR = 60
const _4_HOURS = 4 * 60
const _1_DAY = 24 * 60
const _3_DAYS = 3 * 24 * 60

export type StopLosOrderFormValues = {
  variant?: StopLossVariant
  stopLossPrice?: string
  sellPrice?: string
  callback?: string
  /**
   * Expiry in minutes
   */
  expiry: number
}

type StopLossOrderFormProps = {
  fromToken?: TokenInfo
  toToken?: TokenInfo
  reserves?: Reserves
  onValuesChange?: (values: StopLosOrderFormValues) => void
}

export type StopLossVariant = "market" | "limit" | "trailing"

export function StopLossOrderForm(props: StopLossOrderFormProps) {
  const { onValuesChange, toToken, fromToken, reserves } = props

  const [variant, setVariant] = React.useState<StopLossVariant>("market")
  const [stopLossPrice, setStopLossPriceState] = React.useState<string>("")
  const [sellPrice, setSellPriceState] = React.useState<string>("")
  const [callback, setCallbackState] = React.useState<string>("")
  // in minutes
  const [expiry, setExpiry] = React.useState<number>(_3_DAYS)

  const [pristine, setPristine] = React.useState(true)

  // market price
  const price = useMemo(() => {
    if (!reserves || !fromToken || !toToken) return null

    const base = reserves[fromToken.id]
    const baseToken = fromToken
    const quote = reserves[toToken.id]
    const quoteToken = toToken

    let price =
      quote === 0n
        ? null
        : Number(quote) /
          10 ** quoteToken.denomination /
          (Number(base) / 10 ** baseToken.denomination)

    return price
  }, [fromToken, toToken, reserves])

  const setStopLossPrice = (value: string) => {
    setStopLossPriceState(value)

    if (!value) return
    if (!sellPrice || Number(value) < Number(sellPrice)) {
      setSellPriceState(value)
    }
  }

  const setSellPrice = (value: string) => {
    if (!stopLossPrice || Number(stopLossPrice) >= Number(value)) {
      setSellPriceState(value)
    }
  }

  useEffect(() => {
    if (price !== null && !isNaN(price) && pristine) {
      setStopLossPrice(truncateNumberAuto(price))
      setCallbackAsPriceInput(truncateNumberAuto(price))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pristine, price])

  const stopLossPriceAsPercentage = useMemo(() => {
    if (!price || !stopLossPrice) return 0

    const stopLossPriceAsNumber = parseFloat(stopLossPrice)
    if (isNaN(stopLossPriceAsNumber)) return 0

    return 100 - parseInt(((stopLossPriceAsNumber / price) * 100).toFixed(0))
  }, [price, stopLossPrice])

  const sellPriceAsPercentage = useMemo(() => {
    if (!price || !sellPrice) return 0

    const stopLossPriceAsNumber = parseFloat(sellPrice)
    if (isNaN(stopLossPriceAsNumber)) return 0

    return 100 - parseInt(((stopLossPriceAsNumber / price) * 100).toFixed(0))
  }, [price, sellPrice])

  const [callbackAsPriceInput, setCallbackAsPriceInput] = React.useState<string>("")

  const setCallback = (value: string) => {
    setCallbackState(value)
    if (!price) return
    setCallbackAsPriceInput(truncateNumberAuto(price * (1 - parseFloat(value) / 100)))
  }

  useEffect(() => {
    if (!onValuesChange) return

    onValuesChange({ variant, stopLossPrice, sellPrice, callback, expiry })
  }, [callback, expiry, onValuesChange, sellPrice, stopLossPrice, variant])

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{
          alignSelf: "center",
          order: -1,
        }}
        gap={1}
      >
        <Typography color="secondary" component="div">
          Stop Loss Order Type:
        </Typography>
        <Tabs
          value={variant}
          onChange={(_event, newValue) => {
            setVariant(newValue)
            onValuesChange?.({ variant: newValue, stopLossPrice, sellPrice, callback, expiry })
          }}
          sx={{
            paddingX: 0.5,
            minHeight: "unset",
            [`& .${tabsClasses.indicator}`]: {
              background: "var(--mui-palette-accent-main) !important",
            },
            [`& .${tabsClasses.flexContainer}`]: {
              gap: 1,
            },
            [`& .${tabClasses.selected}`]: {
              color: "var(--mui-palette-accent-main) !important",
            },
            [`& .${tabClasses.root}:hover`]: {
              color: "var(--mui-palette-accent-main)",
            },
            [`& .${tabClasses.root}`]: {
              fontSize: (theme) => theme.typography.body2.fontSize,
              minHeight: "unset",
              minWidth: "unset",
              padding: 0.5,
              transition: "none",
            },
          }}
        >
          <Tab value={"market" satisfies StopLossVariant} label="Market" />
          <Tab value={"limit" satisfies StopLossVariant} label="Limit" />
          <Tab value={"trailing" satisfies StopLossVariant} label="Trailing" />
        </Tabs>
      </Stack>
      {variant !== "trailing" && (
        <Stack
          sx={{
            background: "var(--mui-palette-background-default)",
            paddingX: 1.25,
            paddingY: 1,
            borderRadius: 1,
          }}
          gap={1}
        >
          <Typography color="secondary" component="div">
            Set Stop Loss Trigger Price
          </Typography>
          {/* <Typography color="secondary" component="div">
            <Stack direction="row" gap={1}>
              When one
              <TokenPill>
                <TokenLogo tx={fromToken?.logo} alt={`${fromToken?.name} logo`} size="small" />
                {formatTicker(fromToken?.ticker)}{" "}
              </TokenPill>{" "}
              is worth:
            </Stack>
          </Typography> */}
          <Stack
            gap={1}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <Input
              className={css`
                font-size: var(--xl-font-size);
                width: 320px;
                flex: 1;
              `}
              type="text"
              placeholder="0"
              value={stopLossPrice}
              onChange={(event) => {
                const nextInput = decimalEnforcer(event.target.value.replace(/,/g, "."))?.replace(
                  /^0+$/,
                  "0",
                )

                if (nextInput === undefined) return

                const formatted = parseFloat(nextInput)
                if (price && !isNaN(formatted) && formatted >= price) return

                setStopLossPrice(nextInput)
              }}
              pattern="^[0-9]*[.,]?[0-9]*$"
            />
            {toToken && fromToken && (
              <Stack direction="row" gap={1} alignItems="center">
                <TokenPill
                  className={css`
                    font-size: 1.5rem;
                  `}
                >
                  <TokenLogo tx={toToken.logo} alt={`${toToken.name} logo`} size="medium" />
                  {formatTicker(toToken.ticker)}
                </TokenPill>
                /
                <TokenPill
                  className={css`
                    font-size: 1.5rem;
                  `}
                >
                  <TokenLogo tx={fromToken.logo} alt={`${fromToken.name} logo`} size="medium" />
                  {formatTicker(fromToken.ticker)}
                </TokenPill>
              </Stack>
            )}
          </Stack>
          <Box sx={{ minHeight: 25 }}>
            {fromToken && toToken && !reserves && <Skeleton height="1.25rem" width="60px" />}
            {fromToken && toToken && reserves && !!price && (
              <Typography
                variant="caption"
                sx={{
                  background: "var(--mui-palette-background-paper)",
                  borderRadius: 5,
                  paddingY: 0.25,
                  paddingX: 0.75,
                }}
                color="secondary"
              >
                {formatNumberAuto(price, INPUT_DECIMALS)} {toToken?.ticker} = Current Price
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{
              // marginLeft: -0.5,
              height: 32,
              "& .active": {
                background: "var(--mui-palette-background-paper)",
                borderRadius: "3px",
              },
              "& > button": {
                fontSize: "0.75rem !important",
              },
            }}
          >
            {!!price && (
              <>
                <SmallButton
                  className={stopLossPriceAsPercentage === 0 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price)
                    setStopLossPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  Market
                </SmallButton>
                <SmallButton
                  className={stopLossPriceAsPercentage === 5 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price * 0.95)
                    setStopLossPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  -5%
                </SmallButton>
                <SmallButton
                  className={stopLossPriceAsPercentage === 10 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price * 0.9)
                    setStopLossPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  -10%
                </SmallButton>
                <SmallButton
                  className={stopLossPriceAsPercentage === 15 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price * 0.85)
                    setStopLossPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  -15%
                </SmallButton>
                <SmallButton
                  className={stopLossPriceAsPercentage === 20 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price * 0.8)
                    setStopLossPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  -20%
                </SmallButton>
              </>
            )}
          </Stack>
          <CustomSlider
            sx={{
              width: "50%",
              marginX: 0.5,
              marginBottom: 1,
              padding: 0,
              visibility: price === null ? "hidden" : "visible",
            }}
            color="accent"
            value={stopLossPriceAsPercentage}
            valueLabelDisplay="auto"
            shiftStep={1}
            step={1}
            min={0}
            max={99}
            valueLabelFormat={(value) => `-${value}%`}
            onChange={(_event, value) => {
              if (!price || isNaN(price)) return

              const nextInput = truncateNumberAuto(price * (1 - (value as number) / 100))
              setStopLossPrice(nextInput)
            }}
          />
        </Stack>
      )}
      {variant === "limit" && (
        <Stack
          sx={{
            background: "var(--mui-palette-background-default)",
            paddingX: 1.25,
            paddingY: 1,
            borderRadius: 1,
          }}
          gap={1}
        >
          <Typography color="secondary" component="div">
            Set Stop Loss Limit Price
          </Typography>
          <Stack
            gap={1}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <Input
              className={css`
                font-size: var(--xl-font-size);
                width: 320px;
                flex: 1;
              `}
              type="text"
              placeholder="0"
              value={sellPrice}
              onChange={(event) => {
                const nextInput = decimalEnforcer(event.target.value.replace(/,/g, "."))?.replace(
                  /^0+$/,
                  "0",
                )

                if (nextInput === undefined) return

                const formatted = parseFloat(nextInput)
                if (price && !isNaN(formatted) && formatted >= price) return

                setSellPrice(nextInput)
              }}
              pattern="^[0-9]*[.,]?[0-9]*$"
            />
            {toToken && fromToken && (
              <Stack direction="row" gap={1} alignItems="center">
                <TokenPill
                  className={css`
                    font-size: 1.5rem;
                  `}
                >
                  <TokenLogo tx={toToken.logo} alt={`${toToken.name} logo`} size="medium" />
                  {formatTicker(toToken.ticker)}
                </TokenPill>
                /
                <TokenPill
                  className={css`
                    font-size: 1.5rem;
                  `}
                >
                  <TokenLogo tx={fromToken.logo} alt={`${fromToken.name} logo`} size="medium" />
                  {formatTicker(fromToken.ticker)}
                </TokenPill>
              </Stack>
            )}
          </Stack>
          <Box sx={{ minHeight: 25 }}>
            {fromToken && toToken && !reserves && <Skeleton height="1.25rem" width="60px" />}
            {fromToken && toToken && reserves && !!price && (
              <Typography
                variant="caption"
                sx={{
                  background: "var(--mui-palette-background-paper)",
                  borderRadius: 5,
                  paddingY: 0.25,
                  paddingX: 0.75,
                }}
                color="secondary"
              >
                {formatNumberAuto(price, INPUT_DECIMALS)} {toToken?.ticker} = Current Price
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{
              // marginLeft: -0.5,
              height: 32,
              "& .active": {
                background: "var(--mui-palette-background-paper)",
                borderRadius: "3px",
              },
              "& > button": {
                fontSize: "0.75rem !important",
              },
            }}
          >
            {!!price && (
              <>
                <SmallButton
                  className={sellPriceAsPercentage === 0 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price)
                    setSellPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  Market
                </SmallButton>
                <SmallButton
                  className={sellPriceAsPercentage === 5 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price * 0.95)
                    setSellPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  -5%
                </SmallButton>
                <SmallButton
                  className={sellPriceAsPercentage === 10 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price * 0.9)
                    setSellPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  -10%
                </SmallButton>
                <SmallButton
                  className={sellPriceAsPercentage === 15 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price * 0.85)
                    setSellPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  -15%
                </SmallButton>
                <SmallButton
                  className={sellPriceAsPercentage === 20 ? "active" : ""}
                  onClick={() => {
                    const nextInput = truncateNumberAuto(price * 0.8)
                    setSellPrice(nextInput)
                    setPristine(false)
                  }}
                >
                  -20%
                </SmallButton>
              </>
            )}
          </Stack>
          <CustomSlider
            sx={{
              width: "50%",
              marginX: 0.5,
              marginBottom: 1,
              padding: 0,
              visibility: price === null ? "hidden" : "visible",
            }}
            color="accent"
            value={sellPriceAsPercentage}
            valueLabelDisplay="auto"
            shiftStep={1}
            step={1}
            min={0}
            max={99}
            valueLabelFormat={(value) => `-${value}%`}
            onChange={(_event, value) => {
              if (!price || isNaN(price)) return

              const nextInput = truncateNumberAuto(price * (1 - (value as number) / 100))
              setSellPrice(nextInput)
            }}
          />
        </Stack>
      )}
      {variant === "trailing" && (
        <Stack
          sx={{
            background: "var(--mui-palette-background-default)",
            paddingX: 1.25,
            paddingY: 1,
            borderRadius: 1,
          }}
          gap={1}
        >
          <Typography color="secondary" component="div">
            Set Trailing Stop Loss
          </Typography>
          <Stack
            gap={1}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <TextField
              variant="filled"
              sx={{
                width: 320,
                height: 60,
                "& .MuiInputBase-root": {
                  background: "transparent !important",
                  height: 60,
                },
                "& .MuiInputBase-root.MuiFilledInput-root:focus-within": {
                  outline: "none !important",
                  background: "transparent",
                },
              }}
              size="medium"
              inputProps={{
                sx: {
                  borderRadius: 1,
                  padding: 0,
                  fontSize: "3.25rem",
                  fontWeight: 500,
                  fontFamily: NumberFont,
                },
              }}
              // InputProps={{
              //   startAdornment: (
              //     <Typography color="text.secondary" fontSize={"3.25rem"}>
              //       ≈
              //     </Typography>
              //   ),
              // }}
              color="secondary"
              type="text"
              placeholder="0"
              value={callbackAsPriceInput}
              onChange={(event) => {
                const nextInput = decimalEnforcer(event.target.value.replace(/,/g, "."))?.replace(
                  /^0+$/,
                  "0",
                )

                if (nextInput === undefined) return

                const formatted = parseFloat(nextInput)
                if (price && !isNaN(formatted) && formatted >= price) return
                setCallbackAsPriceInput(nextInput)

                if (!price) return
                // convert to percentage
                const percentage = ((price - formatted) / price) * 100
                setCallbackState(percentage.toFixed(0))
              }}
            />
            <Stack sx={{ flex: 2, alignSelf: "flex-start" }} gap={0.5}>
              <Typography color="secondary" variant="caption">
                Trailing percentage
              </Typography>
              <TextField
                variant="filled"
                sx={{
                  width: 80,
                }}
                size="medium"
                inputProps={{
                  sx: {
                    textAlign: "right",
                    borderRadius: 1,
                    paddingY: 0.25,
                    paddingX: 0.75,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <Typography marginRight={-0.5} color="text.secondary">
                      -
                    </Typography>
                  ),
                  endAdornment: (
                    <Typography marginLeft={0.5} color="text.secondary">
                      %
                    </Typography>
                  ),
                }}
                color="secondary"
                type="number"
                placeholder="0"
                value={callback}
                onChange={(event) => {
                  const nextInput = decimalEnforcer(event.target.value.replace(/,/g, "."))?.replace(
                    /^0+$/,
                    "0",
                  )
                  if (nextInput !== undefined) {
                    setCallback(nextInput)
                  }
                }}
              />
            </Stack>
            {toToken && fromToken && (
              <Stack direction="row" gap={1} alignItems="center">
                <TokenPill
                  className={css`
                    font-size: 1.5rem;
                  `}
                >
                  <TokenLogo tx={toToken.logo} alt={`${toToken.name} logo`} size="medium" />
                  {formatTicker(toToken.ticker)}
                </TokenPill>
                /
                <TokenPill
                  className={css`
                    font-size: 1.5rem;
                  `}
                >
                  <TokenLogo tx={fromToken.logo} alt={`${fromToken.name} logo`} size="medium" />
                  {formatTicker(fromToken.ticker)}
                </TokenPill>
              </Stack>
            )}
          </Stack>
          <Box sx={{ minHeight: 25 }}>
            {fromToken && toToken && !reserves && <Skeleton height="1.25rem" width="60px" />}
            {fromToken && toToken && reserves && !!price && (
              <Typography
                variant="caption"
                sx={{
                  background: "var(--mui-palette-background-paper)",
                  borderRadius: 5,
                  paddingY: 0.25,
                  paddingX: 0.75,
                }}
                color="secondary"
              >
                {formatNumberAuto(price, INPUT_DECIMALS)} {toToken?.ticker} = Current Price
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{
              // marginLeft: -0.5,
              height: 32,
              "& .active": {
                background: "var(--mui-palette-background-paper)",
                borderRadius: "3px",
              },
              "& > button": {
                fontSize: "0.75rem !important",
              },
            }}
          >
            {!!price && (
              <>
                <SmallButton
                  className={Number(callback) === 0 ? "active" : ""}
                  onClick={() => {
                    setCallback("0")
                    setPristine(false)
                  }}
                >
                  Market
                </SmallButton>
                <SmallButton
                  className={Number(callback) === 5 ? "active" : ""}
                  onClick={() => {
                    setCallback("5")
                    setPristine(false)
                  }}
                >
                  -5%
                </SmallButton>
                <SmallButton
                  className={Number(callback) === 10 ? "active" : ""}
                  onClick={() => {
                    setCallback("10")
                    setPristine(false)
                  }}
                >
                  -10%
                </SmallButton>
                <SmallButton
                  className={Number(callback) === 15 ? "active" : ""}
                  onClick={() => {
                    setCallback("15")
                    setPristine(false)
                  }}
                >
                  -15%
                </SmallButton>
                <SmallButton
                  className={Number(callback) === 20 ? "active" : ""}
                  onClick={() => {
                    setCallback("20")
                    setPristine(false)
                  }}
                >
                  -20%
                </SmallButton>
              </>
            )}
          </Stack>
          <CustomSlider
            sx={{
              width: "50%",
              marginX: 0.5,
              marginBottom: 1,
              padding: 0,
              visibility: price === null ? "hidden" : "visible",
            }}
            color="accent"
            value={Number(callback)}
            valueLabelDisplay="auto"
            shiftStep={1}
            step={1}
            min={0}
            max={99}
            valueLabelFormat={(value) => `-${value}%`}
            onChange={(_event, value) => {
              setCallback(value.toString())
            }}
          />
        </Stack>
      )}
      <Stack
        gap={1}
        direction="row"
        justifyContent="space-between"
        sx={{
          minHeight: 30,
          marginLeft: 1,
        }}
        alignItems="center"
      >
        <Typography variant="body2" color="text.secondary">
          Expiry
        </Typography>
        <Stack
          direction="row"
          sx={{
            marginBottom: -0.25,
            "& button": {
              borderRadius: "3px !important",
            },
            "& button:not(.active)": {
              background: "none",
            },
          }}
        >
          <SmallButton
            className={expiry === _1_HOUR ? "active" : ""}
            onClick={() => {
              setExpiry(_1_HOUR)
              onValuesChange?.({ expiry: _1_HOUR, variant, stopLossPrice, sellPrice, callback })
            }}
          >
            1 HOUR
          </SmallButton>
          <SmallButton
            className={expiry === _4_HOURS ? "active" : ""}
            onClick={() => {
              setExpiry(_4_HOURS)
              onValuesChange?.({ expiry: _4_HOURS, variant, stopLossPrice, sellPrice, callback })
            }}
          >
            4 HOURS
          </SmallButton>
          <SmallButton
            className={expiry === _1_DAY ? "active" : ""}
            onClick={() => {
              setExpiry(_1_DAY)
              onValuesChange?.({ expiry: _1_DAY, variant, stopLossPrice, sellPrice, callback })
            }}
          >
            1 DAY
          </SmallButton>
          <SmallButton
            className={expiry === _3_DAYS ? "active" : ""}
            onClick={() => {
              setExpiry(_3_DAYS)
              onValuesChange?.({ expiry: _3_DAYS, variant, stopLossPrice, sellPrice, callback })
            }}
          >
            3 DAYS
          </SmallButton>
        </Stack>
      </Stack>
    </>
  )
}
